<section class="onboarding-page">
  <section class="onboarding-hero">
    <div class="hero-copy">
      <span class="hero-icon material-symbols-outlined">domain_add</span>
      <div><p>Seller verification</p><h1>Build your ZepBazaar business workspace</h1><span>One secure application unlocks locations, inventory, sales and reporting after approval.</span></div>
    </div>
    <div class="progress-track" aria-label="Onboarding progress">
      <div class="progress-step active"><strong>1</strong><span>Business details</span></div>
      <i></i>
      <div class="progress-step"><strong>2</strong><span>Admin review</span></div>
      <i></i>
      <div class="progress-step"><strong>3</strong><span>Select plan</span></div>
    </div>
  </section>
  <header class="page-heading">
    <div>
      <p>Business setup</p>
      <h2>Complete your business profile</h2>
      <span>Submit your business details and verification documents for platform review.</span>
    </div>
    <span class="step-indicator">Step 1 of 2</span>
  </header>

  @if (errorMessage) {
    <div class="state error"><span>{{ errorMessage }}</span>@if (!hasSubmittedProfile) { <button type="button" (click)="loadOnboarding()">Try again</button> }</div>
  }
  @if (isCorrectionMode) {
    <div class="state correction"><strong>Corrections requested</strong><span>{{ approvalReason }}</span><small>Update the requested information and resubmit it for a fresh admin review.</small></div>
  }

  @if (isLoading) {
    <section class="loading-shell" aria-label="Loading business setup">
      <div class="loading-title"><span></span><div><i></i><i></i></div></div>
      <div class="loading-grid">@for (item of [1,2,3,4]; track item) { <article><span></span><i></i><i></i></article> }</div>
    </section>
  } @else if (hasSubmittedProfile) {
    <section class="form-section approval-state">
      <span class="material-symbols-outlined" aria-hidden="true">hourglass_top</span>
      <p>Application status</p>
      <h3>{{ approvalStatus }}</h3>
      @if (approvalStatus === 'Pending') {
        <span
          >Your business information was submitted. Platform Admin will review it before operational
          access is enabled.</span
        >
      }
      @if (approvalStatus === 'OnHold') {
        <span>Your application is temporarily on hold. Please review the administrator note.</span>
      }
      @if (approvalStatus === 'CorrectionRequested') {
        <span>Corrections were requested. Please update the noted details and resubmit.</span>
      }
      @if (approvalStatus === 'Rejected') {
        <span>The application was not approved. Correct the noted issue and contact support.</span>
      }
      @if (approvalReason) {
        <strong>Admin note: {{ approvalReason }}</strong>
      }
    </section>
  } @else {
    <form (ngSubmit)="submit()">
      <section class="form-section">
        <div class="section-heading">
          <span class="material-symbols-outlined" aria-hidden="true">verified_user</span>
          <div><h3>Verification documents</h3><p>PDF, JPG or PNG · maximum 8 MB per file</p></div>
        </div>
        <div class="document-grid">
          @for (document of documentSlots; track document.type; let index = $index) {
            <article class="document-card" [class.selected]="document.file">
              <div class="document-title">
                <span class="material-symbols-outlined">{{ document.file?.type?.includes('pdf') ? 'picture_as_pdf' : 'description' }}</span>
                <div><strong>{{ document.name }}</strong><small>Required</small></div>
              </div>
              <input class="document-number" [(ngModel)]="document.number" [name]="'documentNumber' + index" placeholder="Document number (optional)">
              @if (document.file) {
                <div class="selected-file">
                  <span>{{ document.file.name }}</span>
                  <div>
                    <a [href]="document.preview" target="_blank" rel="noopener">Preview</a>
                    <button type="button" (click)="removeDocument(index)">Remove</button>
                  </div>
                </div>
              } @else if (document.existingUrl) {
                <div class="selected-file existing"><span>Previously submitted</span><div><a [href]="document.existingUrl" target="_blank" rel="noopener">View file</a><label class="replace-action">Replace<input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" (change)="selectDocument(index, $event)"></label></div></div>
              } @else {
                <label class="upload-action">
                  <span class="material-symbols-outlined">upload_file</span> Choose document
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" (change)="selectDocument(index, $event)">
                </label>
              }
            </article>
          }
        </div>
      </section>

      <section class="form-section">
        <div class="section-heading">
          <span class="material-symbols-outlined" aria-hidden="true">domain</span>
          <div>
            <h3>Business details</h3>
            <p>Legal and customer-facing information</p>
          </div>
        </div>
        <div class="form-grid">
          <label
            ><span>Business type</span
            ><select name="businessTypeId" [(ngModel)]="form.businessTypeId" required [class.invalid]="fieldErrors['businessTypeId']">
              <option value="" disabled>Select business type</option>
              @for (type of businessTypes; track type.id) {
                <option [value]="type.id">{{ type.name }}</option>
              }
            </select>@if (fieldErrors['businessTypeId']) { <small class="field-error">{{ fieldErrors['businessTypeId'] }}</small> }</label
          >
          <label
            ><span>Legal business name</span
            ><input
              name="legalName"
              [(ngModel)]="form.legalName"
              minlength="2" maxlength="200"
              required
              [class.invalid]="fieldErrors['legalName']"
              placeholder="Name on registration documents"
          />@if (fieldErrors['legalName']) { <small class="field-error">{{ fieldErrors['legalName'] }}</small> }</label>
          <label
            ><span>Trading name</span
            ><input
              name="tradingName"
              [(ngModel)]="form.tradingName"
              maxlength="200"
              placeholder="Customer-facing name"
          /></label>
          <label
            ><span>CIN (companies only)</span
            ><input
              name="registrationNumber"
              [(ngModel)]="form.registrationNumber"
              maxlength="21"
              placeholder="Example: L12345UP2024PLC123456"
              [class.invalid]="fieldErrors['registrationNumber']"
              (input)="sanitizeCin()"
          />@if (fieldErrors['registrationNumber']) { <small class="field-error">{{ fieldErrors['registrationNumber'] }}</small> }</label>
          <label
            ><span>GSTIN</span
            ><input name="taxNumber" [(ngModel)]="form.taxNumber" maxlength="15"
              placeholder="15-character GSTIN" [class.invalid]="fieldErrors['taxNumber']"
              (input)="sanitizeGstin()"
          />@if (fieldErrors['taxNumber']) { <small class="field-error">{{ fieldErrors['taxNumber'] }}</small> }</label>
          <label
            ><span>Website</span
            ><input
              type="url"
              name="website"
              [(ngModel)]="form.website"
              maxlength="300"
              placeholder="https://example.in"
              [class.invalid]="fieldErrors['website']"
              (blur)="normalizeWebsite()"
          />@if (fieldErrors['website']) { <small class="field-error">{{ fieldErrors['website'] }}</small> }</label>
          <label
            ><span>Business email</span
            ><input
              type="email"
              name="email"
              [(ngModel)]="form.email"
              maxlength="256"
              placeholder="business@example.in"
              [class.invalid]="fieldErrors['email']"
          />@if (fieldErrors['email']) { <small class="field-error">{{ fieldErrors['email'] }}</small> }</label>
          <label
            ><span>Business phone</span
            ><input
              name="phone"
              [(ngModel)]="form.phone"
              inputmode="numeric"
              autocomplete="tel-national"
              maxlength="10"
              pattern="[6-9][0-9]{9}"
              placeholder="10-digit mobile number"
              [class.invalid]="fieldErrors['phone']"
              (input)="sanitizePhone()"
              required
          />@if (fieldErrors['phone']) { <small class="field-error">{{ fieldErrors['phone'] }}</small> }</label>
        </div>
      </section>

      <section class="form-section">
        <div class="section-heading">
          <span class="material-symbols-outlined" aria-hidden="true">location_on</span>
          <div>
            <h3>Registered address</h3>
            <p>Your first selling location is added separately on the next step</p>
          </div>
        </div>
        <div class="google-address-search">
          <label class="wide"><span>Search registered address</span>
            <div class="address-search-control">
              <span class="material-symbols-outlined">location_on</span>
              <input name="addressSearch" [ngModel]="addressSearch" (ngModelChange)="searchAddress($event)"
                autocomplete="off" placeholder="Search building, road, area, city or PIN code"
                [class.invalid]="fieldErrors['address']" />
              @if (isAddressSearching || isAddressResolving) { <span class="material-symbols-outlined spinning">progress_activity</span> }
            </div>
            @if (fieldErrors['address']) { <small class="field-error">{{ fieldErrors['address'] }}</small> }
          </label>
          @if (addressSuggestions.length) {
            <div class="address-suggestions">
              @for (suggestion of addressSuggestions; track suggestion.placeId) {
                <button type="button" (click)="selectAddress(suggestion)"><span class="material-symbols-outlined">location_on</span><span>{{ suggestion.description }}</span></button>
              }
            </div>
          }
          @if (addressError) { <p class="address-error">{{ addressError }}</p> }
          @if (selectedAddress) { <div class="selected-address"><span class="material-symbols-outlined">where_to_vote</span><div><strong>Address selected from Google</strong><span>{{ selectedAddress }}</span></div></div> }
        </div>
        <div class="form-grid">
          <label class="wide"
            ><span>Google formatted address</span
            ><input
              name="addressLine1"
              [(ngModel)]="form.addressLine1"
              placeholder="Select an address above"
              readonly
          /></label>
          <label class="wide"
            ><span>Floor, unit or landmark (optional)</span
            ><input
              name="addressLine2"
              [(ngModel)]="form.addressLine2"
              placeholder="Area or landmark"
          /></label>
          <label><span>City</span><input name="city" [(ngModel)]="form.city" maxlength="120" readonly [class.invalid]="fieldErrors['city']" />@if (fieldErrors['city']) { <small class="field-error">{{ fieldErrors['city'] }}</small> }</label>
          <label><span>State</span><input name="state" [(ngModel)]="form.state" maxlength="120" readonly [class.invalid]="fieldErrors['state']" />@if (fieldErrors['state']) { <small class="field-error">{{ fieldErrors['state'] }}</small> }</label>
          <label
            ><span>PIN code</span
            ><input
              name="postalCode"
              [(ngModel)]="form.postalCode"
              inputmode="numeric"
              maxlength="6"
              pattern="[1-9][0-9]{5}"
              placeholder="6-digit PIN code"
              [class.invalid]="fieldErrors['postalCode']"
              (input)="sanitizePostalCode()"
              required
          />@if (fieldErrors['postalCode']) { <small class="field-error">{{ fieldErrors['postalCode'] }}</small> }</label>
          <label><span>Country</span><input value="India" readonly /></label>
        </div>
      </section>

      <section class="form-section">
        <div class="section-heading">
          <span class="material-symbols-outlined" aria-hidden="true">account_balance</span>
          <div>
            <h3>Primary settlement account</h3>
            <p>Used for future business payouts and accounting</p>
          </div>
        </div>
        <div class="form-grid">
          <label
            ><span>Bank name</span><input name="bankName" [(ngModel)]="form.bankName" minlength="2" maxlength="150" required [class.invalid]="fieldErrors['bankName']"
          />@if (fieldErrors['bankName']) { <small class="field-error">{{ fieldErrors['bankName'] }}</small> }</label>
          <label
            ><span>Branch</span><input name="branchName" [(ngModel)]="form.branchName" maxlength="150"
          /></label>
          <label
            ><span>Account holder</span
            ><input name="accountHolderName" [(ngModel)]="form.accountHolderName" minlength="2" maxlength="200" required [class.invalid]="fieldErrors['accountHolderName']"
          />@if (fieldErrors['accountHolderName']) { <small class="field-error">{{ fieldErrors['accountHolderName'] }}</small> }</label>
          <label
            ><span>Account number</span
            ><input
              name="accountNumber"
              [(ngModel)]="form.accountNumber"
              required
              inputmode="numeric"
              minlength="9"
              maxlength="18"
              pattern="[0-9]{9,18}"
              placeholder="9 to 18 digits"
              [class.invalid]="fieldErrors['accountNumber']"
              (input)="sanitizeAccountNumber()"
          />@if (fieldErrors['accountNumber']) { <small class="field-error">{{ fieldErrors['accountNumber'] }}</small> }</label>
          <label
            ><span>IFSC code</span
            ><input name="ifscCode" [(ngModel)]="form.ifscCode" required class="uppercase"
              minlength="11" maxlength="11" pattern="[A-Z]{4}0[A-Z0-9]{6}"
              placeholder="Example: HDFC0001234"
              [class.invalid]="fieldErrors['ifscCode']"
              (input)="sanitizeIfsc()"
          />@if (fieldErrors['ifscCode']) { <small class="field-error">{{ fieldErrors['ifscCode'] }}</small> }</label>
          <label><span>Currency</span><input value="INR" readonly /></label>
        </div>
      </section>

      <section class="consent-card" [class.invalid]="fieldErrors['termsAccepted']">
        <label><input type="checkbox" name="termsAccepted" [(ngModel)]="termsAccepted" (change)="fieldErrors['termsAccepted'] = ''"><span>I confirm that the submitted information is accurate and I accept the <a href="/legal/terms" target="_blank">Terms of Use</a> and <a href="/legal/privacy" target="_blank">Privacy Policy</a>.</span></label>
        @if (fieldErrors['termsAccepted']) { <small class="field-error">{{ fieldErrors['termsAccepted'] }}</small> }
      </section>

      <div class="form-actions">
        <button type="submit" [disabled]="isSubmitting || !businessTypes.length">
          <span>{{ isSubmitting ? (isCorrectionMode ? 'Resubmitting corrections...' : 'Saving business...') : (isCorrectionMode ? 'Resubmit for review' : 'Save and add location') }}</span>
          <span class="material-symbols-outlined" aria-hidden="true">arrow_forward</span>
        </button>
      </div>
    </form>
  }
</section>
